import React, { Component, ComponentType, PropsWithChildren } from 'react'
import { IErrorViewDefaultProps, IHocLazyWrapperProps } from './ui.units'
import { ELazyStatus, IDinoReduxDispatch, IDinoReduxState, IReturnDispatch } from './types'
import UIErrorPage from './ui.error-page'

export interface IHocLazyBase<T> extends IDinoReduxState, IDinoReduxDispatch<T> {}

export interface IHocLazyBaseOptions {
  ErrorScreen?: React.ComponentType<IErrorViewDefaultProps>
  LazySpinner: React.ComponentType<PropsWithChildren<IHocLazyWrapperProps>>
}

export interface IHocLazyOtherProps<T> {
  param?: T
}

export interface IHocLazyOptions<T> extends IHocLazyOtherProps<T>, Omit<IHocLazyBaseOptions, 'LazySpinner'> {
  LazySpinner?: React.ComponentType<PropsWithChildren<IHocLazyWrapperProps>>
}

export const EmptyComponent: React.FC<React.PropsWithChildren<any>> = ({ children }) => <>{children}</>

export const defaultOptionHocLazy: IHocLazyBaseOptions = {
  LazySpinner: EmptyComponent
}

const CreateHocLazy = function <P extends IHocLazyBase<T>, T = any>(
  ChildComponent: React.ComponentType<P>,
  options?: IHocLazyOptions<T>
): ComponentType<P> {
  type TProps = P & IHocLazyOtherProps<T>
  class HocComponent extends Component<TProps> {
    optionsMerge: IHocLazyBaseOptions
    tokenSources?: AbortController
    fetchSource?: IReturnDispatch
    constructor(props: TProps) {
      super(props)
      this.optionsMerge = Object.assign({}, defaultOptionHocLazy, options) as IHocLazyBaseOptions
    }

    render() {
      const { optionsMerge: OptionsMerge } = this
      switch (this.props.status) {
        case ELazyStatus.Loading:
        case ELazyStatus.Loaded:
          return (
            <OptionsMerge.LazySpinner loading={this.props.status === ELazyStatus.Loading}>
              <ChildComponent {...this.props} />
            </OptionsMerge.LazySpinner>
          )
        case ELazyStatus.Error:
          const ErrorScreen = OptionsMerge.ErrorScreen || UIErrorPage
          return <ErrorScreen />
        default:
          return <div></div>
      }
    }

    initialParam = (): T => {
      const oData = options?.param ?? {}
      const pData = this.props?.param ?? {}
      return Object.assign({}, oData, pData) as T
    }

    componentDidMount = () => {
      if (this.props.fetchData) {
        const param = this.initialParam()
        if (this.tokenSources) this.tokenSources.abort()
        const controller = new AbortController()
        this.tokenSources = controller
        this.fetchSource = this.props.fetchData(param, controller.signal)
      }
    }

    componentWillUnmount() {
      if (this.tokenSources) this.tokenSources.abort()
      if (this.fetchSource && typeof this.fetchSource.abort === 'function') {
        this.fetchSource.abort()
      }
    }
  }
  return HocComponent
}
export default CreateHocLazy
